import { useSeo } from "@/lib/seo";

import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Mail, Lock, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import AppleSignInButton from "@/components/AppleSignInButton";

export default function Login() {
  useSeo({
    title: "Log In | Vyrti",
    description: "Log in to Vyrti to manage your listings, trade offers and messages.",
    path: "/login",
  });
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password");
    } finally { setBusy(false); }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="section-pad py-12 md:py-20 grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]" data-testid="login-page">
      <div className="max-w-md w-full mx-auto">
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Welcome back</h1>
        <p className="text-zinc-500 mt-2">Sign in to start trading.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1.5">Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input type="email" required className="input-text !pl-10" placeholder="you@vyrti.com" value={email} onChange={e => setEmail(e.target.value)} data-testid="login-email" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium">Password</label>
              <Link to="/forgot-password" className="text-xs text-[#0654BA] hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input type="password" required className="input-text !pl-10" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} data-testid="login-password" />
            </div>
          </div>
          <button type="submit" disabled={busy} className="btn-pill btn-accent w-full !py-3 disabled:opacity-50" data-testid="login-submit">
            {busy ? "Signing in…" : "Sign in"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-zinc-400">
          <div className="flex-1 h-px bg-[#E2E2E2]" /> or <div className="flex-1 h-px bg-[#E2E2E2]" />
        </div>

        <div className="space-y-3">
          <button onClick={googleSignIn} type="button" className="btn-block w-full !py-3 bg-white text-zinc-900 border border-[#E2E2E2] hover:border-zinc-400" data-testid="login-google">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>
          <AppleSignInButton />
        </div>

        <p className="text-sm text-zinc-500 mt-6 text-center">
          New to Vyrti? <Link to="/signup" className="text-[#0654BA] hover:underline font-medium" data-testid="login-signup-link">Create account</Link>
        </p>
      </div>

      <div className="hidden lg:block">
        <div className="grid grid-cols-2 gap-4">
          <img src="https://images.unsplash.com/photo-1611186871348-b1ce696e52c9?w=600" alt="" className="w-full aspect-square object-cover rounded-3xl shadow-[0_20px_40px_rgb(0,0,0,0.08)]" />
          <img src="https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=600" alt="" className="w-full aspect-square object-cover rounded-3xl shadow-[0_20px_40px_rgb(0,0,0,0.08)] mt-12" />
          <img src="https://images.unsplash.com/photo-1583394838336-acd977736f90?w=600" alt="" className="w-full aspect-square object-cover rounded-3xl shadow-[0_20px_40px_rgb(0,0,0,0.08)] -mt-4" />
          <img src="https://images.unsplash.com/photo-1542272604-787c3835535d?w=600" alt="" className="w-full aspect-square object-cover rounded-3xl shadow-[0_20px_40px_rgb(0,0,0,0.08)]" />
        </div>
      </div>
    </div>
  );
}
